import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, Circle, ChevronDown, ChevronRight, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  entity: { name: "Entity & Brand Building", color: "bg-blue-500" },
  content: { name: "Authority Content", color: "bg-purple-500" },
  platform: { name: "Platform Presence", color: "bg-green-500" },
  reviews: { name: "Review & Reputation", color: "bg-yellow-500" },
  ai_optimization: { name: "AI-Specific Optimization", color: "bg-red-500" },
  local_search: { name: "Local Search & Map Pack", color: "bg-orange-500" },
  tracking: { name: "Tracking & Competitive Intel", color: "bg-slate-500" }
};

const PHASES = {
  1: { name: "Foundation", months: "1-3" },
  2: { name: "Build", months: "4-6" },
  3: { name: "Accelerate", months: "7-9" },
  4: { name: "Dominate", months: "10-12" }
};

function BlueprintDashboard() {
  const { blueprintId } = useParams();
  const [blueprint, setBlueprint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({ 1: true });
  const [updating, setUpdating] = useState(null);
  
  useEffect(() => {
    const fetchBlueprint = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blueprints/${blueprintId}`);
        if (res.ok) {
          const data = await res.json();
          setBlueprint(data);
        } else {
          setError('Blueprint not found');
        }
      } catch (err) {
        setError('Error loading blueprint');
      } finally {
        setLoading(false);
      }
    };
    fetchBlueprint();
  }, [blueprintId]);
  
  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };
  
  const toggleTask = async (taskId, completed) => {
    setUpdating(taskId);
    try {
      const res = await fetch(`${API_URL}/api/blueprints/${blueprintId}/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: !completed })
      });
      if (res.ok) {
        fetchBlueprint();
      }
    } catch (err) {
      console.error('Error updating task:', err);
    } finally {
      setUpdating(null);
    }
  };
  
  const getTasksByMonth = (month) => {
    if (!blueprint?.tasks) return [];
    return blueprint.tasks.filter(t => t.month === month);
  };
  
  const getMonthStats = (month) => {
    const tasks = getTasksByMonth(month);
    const completed = tasks.filter(t => t.completed).length;
    return { total: tasks.length, completed };
  };
  
  const getOverallStats = () => {
    if (!blueprint?.tasks) return { total: 0, completed: 0, percentage: 0 };
    const total = blueprint.tasks.length;
    const completed = blueprint.tasks.filter(t => t.completed).length;
    return { total, completed, percentage: Math.round((completed / total) * 100) };
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-red-600">{error}</p>
          <Button className="mt-4" onClick={() => window.location.href = '/'}>
            Go Home
          </Button>
        </Card>
      </div>
    );
  }
  
  const stats = getOverallStats();
  const isDFY = blueprint.client_type === 'dfy';
  const gapData = blueprint.gap_analysis_data;
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Upgrade Banner for Blueprint Only clients */}
      {!isDFY && (
        <div className="bg-gradient-to-r from-[#d4af37] to-[#b5952f] text-[#0a2540] py-3 px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🚀</span>
              <div>
                <p className="font-semibold">This is your 12-month AI Visibility roadmap</p>
                <p className="text-sm opacity-90">{stats.total} tasks. Ready to have our team execute it for you?</p>
              </div>
            </div>
            <a 
              href="https://cal.com/mark-earle-6tzgnf/60min" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button className="bg-[#0a2540] text-white hover:bg-[#162536]" data-testid="upgrade-dfy-btn">
                Book Implementation Call
              </Button>
            </a>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-[#0a2540] text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold">{blueprint.business_name}</h1>
            {isDFY && (
              <Badge className="bg-[#d4af37] text-[#0a2540]">DFY Client</Badge>
            )}
          </div>
          <p className="text-slate-300 mt-1">12-Month AI Visibility Blueprint</p>
          <div className="flex gap-4 mt-4 text-sm">
            <span className="bg-white/10 px-3 py-1 rounded">{blueprint.primary_location}</span>
            <span className="bg-white/10 px-3 py-1 rounded">{blueprint.services?.join(', ')}</span>
          </div>
        </div>
      </div>
      
      {/* WHERE YOU ARE NOW - Shows when gap analysis data exists */}
      {gapData && (
        <div className="bg-gradient-to-r from-red-50 to-yellow-50 border-b border-red-200">
          <div className="max-w-6xl mx-auto py-6 px-4">
            <h2 className="text-lg font-bold text-[#0a2540] mb-4 flex items-center gap-2">
              📍 Where You Are Now
              <span className="text-xs font-normal text-slate-500">(Based on Gap Analysis)</span>
            </h2>
            <div className="grid grid-cols-2 gap-6">
              {/* Current Score */}
              <div className="flex items-center gap-6">
                <div className={`w-24 h-24 rounded-xl flex flex-col items-center justify-center text-white ${gapData.overall_status === 'red' ? 'bg-red-500' : gapData.overall_status === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'}`}>
                  <span className="text-3xl font-bold">{gapData.overall_score}</span>
                  <span className="text-xs opacity-80">/100</span>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Current Visibility Score</p>
                  <div className="flex gap-3 mt-2 text-sm">
                    <span className="text-red-600 font-medium">{gapData.critical_findings} critical</span>
                    <span className="text-yellow-600 font-medium">{gapData.warning_findings} warnings</span>
                  </div>
                </div>
              </div>
              
              {/* Where You'll Be */}
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 rounded-xl flex flex-col items-center justify-center bg-green-500 text-white">
                  <span className="text-3xl font-bold">85+</span>
                  <span className="text-xs opacity-80">/100</span>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Target After 12 Months</p>
                  <p className="text-green-600 font-medium text-sm mt-2">
                    +{85 - gapData.overall_score} point improvement
                  </p>
                </div>
              </div>
            </div>
            
            {/* Category Mini Bars */}
            <div className="mt-4 pt-4 border-t border-red-200/50">
              <p className="text-xs text-slate-500 mb-2">Category Scores</p>
              <div className="grid grid-cols-7 gap-2">
                {gapData.category_scores?.map((cat) => (
                  <div key={cat.category} className="text-center">
                    <div className="h-12 bg-slate-200 rounded relative overflow-hidden">
                      <div 
                        className={`absolute bottom-0 w-full ${cat.status === 'red' ? 'bg-red-500' : cat.status === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'}`}
                        style={{ height: `${cat.score}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1 truncate" title={cat.category_name}>
                      {cat.category_name?.split(' ')[0]}
                    </p>
                    <p className="text-xs font-medium">{cat.score}%</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Progress Bar - Only for DFY clients */}
      {isDFY && (
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-6xl mx-auto py-4 px-4">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-[#0a2540]">Overall Progress</span>
              <span className="text-slate-600">{stats.completed} of {stats.total} tasks ({stats.percentage}%)</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div 
                className="bg-[#d4af37] h-3 rounded-full transition-all duration-500"
                style={{ width: `${stats.percentage}%` }}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Task count summary for Blueprint Only */}
      {!isDFY && (
        <div className="bg-white border-b">
          <div className="max-w-6xl mx-auto py-4 px-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-[#0a2540]">Your Complete Roadmap</span>
              <span className="text-slate-600 font-medium">{stats.total} tasks across 12 months</span>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-6xl mx-auto py-8 px-4">
        {/* Phase Overview */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {Object.entries(PHASES).map(([phaseNum, phase]) => {
            const phaseMonths = [parseInt(phaseNum) * 3 - 2, parseInt(phaseNum) * 3 - 1, parseInt(phaseNum) * 3];
            const phaseTasks = blueprint.tasks?.filter(t => phaseMonths.includes(t.month)) || [];
            const phaseCompleted = phaseTasks.filter(t => t.completed).length;
            const phasePercentage = phaseTasks.length ? Math.round((phaseCompleted / phaseTasks.length) * 100) : 0;
            
            return (
              <Card key={phaseNum} className="text-center">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 uppercase">Phase {phaseNum}</p>
                  <p className="font-bold text-[#0a2540]">{phase.name}</p>
                  <p className="text-xs text-slate-500">Months {phase.months}</p>
                  {isDFY ? (
                    <div className="mt-2 text-2xl font-bold text-[#d4af37]">{phasePercentage}%</div>
                  ) : (
                    <div className="mt-2 text-lg font-medium text-slate-500">{phaseTasks.length} tasks</div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Monthly Breakdown */}
        <div className="space-y-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => {
            const monthStats = getMonthStats(month);
            const tasks = getTasksByMonth(month);
            const isExpanded = expandedMonths[month];
            const phase = Math.ceil(month / 3);
            const monthPercentage = monthStats.total ? Math.round((monthStats.completed / monthStats.total) * 100) : 0;
            
            return (
              <Card key={month} className="overflow-hidden" data-testid={`month-${month}`}>
                <div 
                  className="flex justify-between items-center p-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => toggleMonth(month)}
                >
                  <div className="flex items-center gap-4">
                    {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    <div>
                      <h3 className="font-semibold text-[#0a2540]">Month {month}: {PHASES[phase]?.name}</h3>
                      {isDFY ? (
                        <p className="text-sm text-slate-500">{monthStats.completed} of {monthStats.total} tasks complete</p>
                      ) : (
                        <p className="text-sm text-slate-500">{monthStats.total} tasks</p>
                      )}
                    </div>
                  </div>
                  {isDFY && (
                    <div className="flex items-center gap-4">
                      <div className="w-32 bg-slate-200 rounded-full h-2">
                        <div 
                          className="bg-[#d4af37] h-2 rounded-full"
                          style={{ width: `${monthPercentage}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-12 text-right">{monthPercentage}%</span>
                    </div>
                  )}
                </div>
                
                {isExpanded && (
                  <CardContent className="border-t pt-4">
                    <div className="space-y-3">
                      {tasks.map((task) => (
                        <div 
                          key={task.id} 
                          className={`flex items-start gap-3 p-3 rounded-lg border ${isDFY && task.completed ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}
                          data-testid={`task-${task.id}`}
                        >
                          {/* Checkbox only for DFY clients */}
                          {isDFY ? (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTask(task.id, task.completed);
                              }}
                              disabled={updating === task.id}
                              className="mt-1"
                            >
                              {updating === task.id ? (
                                <Loader2 className="w-5 h-5 animate-spin text-[#d4af37]" />
                              ) : task.completed ? (
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                              ) : (
                                <Circle className="w-5 h-5 text-slate-300 hover:text-[#d4af37]" />
                              )}
                            </button>
                          ) : (
                            <div className="mt-1">
                              <div className="w-5 h-5 rounded-full border-2 border-slate-300 flex items-center justify-center">
                                <span className="text-xs text-slate-400">•</span>
                              </div>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`font-medium ${isDFY && task.completed ? 'line-through text-slate-500' : 'text-[#0a2540]'}`}>
                                {task.title}
                              </p>
                              <div className="flex gap-1 flex-shrink-0">
                                {task.quick_win && (
                                  <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                                    Quick Win
                                  </Badge>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  {task.priority}
                                </Badge>
                              </div>
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{task.description}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Badge 
                                variant="secondary" 
                                className={`text-xs text-white ${CATEGORIES[task.category]?.color || 'bg-slate-500'}`}
                              >
                                {CATEGORIES[task.category]?.name || task.category}
                              </Badge>
                              {task.target_llms?.map((llm) => (
                                <Badge key={llm} variant="outline" className="text-xs">
                                  {llm}
                                </Badge>
                              ))}
                            </div>
                            {task.why_it_matters && (
                              <p className="text-xs text-slate-500 mt-2 italic">
                                Why: {task.why_it_matters}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
        
        {/* Category Legend */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg">Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(CATEGORIES).map(([key, cat]) => (
                <Badge key={key} className={`${cat.color} text-white`}>
                  {cat.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default BlueprintDashboard;
